import type { z } from 'zod';
import type { MatchReplaceRuleInputSchema } from '@terra/shared-dto-chat-types';
import {
  normalizeMatchReplaceRuleInput,
  type MatchReplaceRuleSummary,
} from '@terra/shared-dto-chat-types';
import {
  buildRuleFromForm,
  resolveRuleOrigins,
  type RuleFormType,
} from '../components/tabs/match-replace-rule-form';
import type { MatchReplaceRule } from '../store/proxy-store';

type MatchReplaceRuleToolRuleInput = z.infer<
  typeof MatchReplaceRuleInputSchema
>;

function mapToolRuleInputToForm({
  ruleInput,
  existingRule,
}: {
  ruleInput: MatchReplaceRuleToolRuleInput;
  existingRule?: MatchReplaceRule | null;
}): RuleFormType {
  const normalizedInput = normalizeMatchReplaceRuleInput(ruleInput);

  return {
    name: normalizedInput.name,
    description: normalizedInput.description ?? '',
    matchType: normalizedInput.matchType,
    matchLocation: normalizedInput.matchLocation,
    matchPattern: normalizedInput.matchPattern,
    isRegex: normalizedInput.isRegex ?? false,
    caseSensitive: normalizedInput.caseSensitive ?? false,
    replaceWith: normalizedInput.replaceWith,
    replaceType: normalizedInput.replaceType ?? 'string',
    enabled: normalizedInput.enabled ?? true,
    headerName: normalizedInput.headerName ?? '',
    applyTo: normalizedInput.applyTo?.length
      ? normalizedInput.applyTo
      : existingRule
        ? resolveRuleOrigins(existingRule)
        : ['proxy'],
    conditionUrlPattern: normalizedInput.conditions?.urlPattern ?? '',
    conditionMethods: normalizedInput.conditions?.methods ?? [],
    conditionStatusCodes:
      normalizedInput.conditions?.statusCodes?.join(', ') ?? '',
    conditionContentTypes:
      normalizedInput.conditions?.contentTypes?.join(', ') ?? '',
  };
}

export function buildMatchReplaceRuleFromToolInput({
  ruleInput,
  existingRule,
}: {
  ruleInput: MatchReplaceRuleToolRuleInput;
  existingRule?: MatchReplaceRule | null;
}): MatchReplaceRule {
  const rule = buildRuleFromForm({
    form: mapToolRuleInputToForm({ ruleInput, existingRule }),
    editingRule: existingRule ?? null,
  });

  if (ruleInput.id && !existingRule) {
    return { ...rule, id: ruleInput.id };
  }

  return rule;
}

export function mapMatchReplaceRuleToSummary(
  rule: MatchReplaceRule,
): MatchReplaceRuleSummary {
  return {
    ruleId: rule.id,
    ruleName: rule.name,
    enabled: rule.enabled,
    matchType: rule.matchType,
    matchLocation: rule.matchLocation,
    matchPattern: rule.matchPattern,
    replaceWith: rule.replaceWith,
    isRegex: rule.isRegex,
    ...(rule.headerName ? { headerName: rule.headerName } : {}),
    ...(rule.applyTo?.length ? { applyTo: rule.applyTo } : {}),
  };
}

export function filterMatchReplaceRulesForList({
  rules,
  nameFilter,
}: {
  rules: MatchReplaceRule[];
  nameFilter?: string;
}): MatchReplaceRule[] {
  const normalizedFilter = nameFilter?.trim().toLowerCase();

  if (!normalizedFilter) {
    return rules;
  }

  return rules.filter((rule) =>
    rule.name.toLowerCase().includes(normalizedFilter),
  );
}
